// ============================================================================
// BOQ — SD area-rate statistics
// ============================================================================
// Pure aggregation for the Schematic-Design area-rate corpus (boq.sd_rates).
// Given a set of historical rates and a cost-index escalation function, this
// escalates each rate to a pricing date and summarises the spread (median /
// quartiles) so a new SD estimate can be primed with a defensible per-sqm rate.
//
// Escalation is delegated to the caller's `adjust(value, fromDate, currency,
// toDate)` (from useBoqCostIndex) so the maths stays identical to the rest of
// the BOQ app. When the index can't price a row, we fall back to the raw rate
// rather than dropping it. All functions are pure for unit-testing.
//
// Rates of different `measureBasis` (carpet vs built-up vs construction area)
// are NOT comparable, so callers should summarise one basis at a time.
// ============================================================================

export const MEASURE_BASIS_LABELS = {
  plan_area: 'Plan area',
  carpet_area: 'Carpet area',
  built_up_area: 'Built-up area',
  construction_area: 'Construction area',
  elevation_area: 'Elevation area',
};

// The building-area bases an SD area-rate can be expressed per. A line is an
// SD area-rate line when it's an allowance carrying one of these as its basis.
export const AREA_BASES = Object.keys(MEASURE_BASIS_LABELS);

export function isAreaBasis(basis) {
  return AREA_BASES.includes(basis);
}

export function measureBasisLabel(basis) {
  return MEASURE_BASIS_LABELS[basis] || basis || '—';
}

// percentile_cont equivalent: linear interpolation between closest ranks on an
// already-sorted ascending array. Mirrors Postgres percentile_cont so the UI
// agrees with any SQL spot-checks.
export function percentileCont(sortedAsc, p) {
  const n = sortedAsc.length;
  if (n === 0) return null;
  if (n === 1) return sortedAsc[0];
  const rank = (n - 1) * p;
  const lo = Math.floor(rank);
  const hi = Math.ceil(rank);
  if (lo === hi) return sortedAsc[lo];
  return sortedAsc[lo] + (rank - lo) * (sortedAsc[hi] - sortedAsc[lo]);
}

// Escalate every rate to `asOfDate` and return summary stats + the contributing
// rows (each annotated with its escalated value and factor). `adjust` and
// `factor` come from useBoqCostIndex; both may return null when the index can't
// price a date, in which case we keep the original rate (no silent zeroing).
export function summarizeRates(rates, { adjust, factor, asOfDate } = {}) {
  const list = Array.isArray(rates) ? rates : [];
  const contributors = list.map((r) => {
    const escalated = adjust ? adjust(r.rate, r.rateDate, r.currency, asOfDate) : null;
    const f = factor ? factor(r.rateDate, r.currency, asOfDate) : null;
    return {
      ...r,
      escalatedRate: escalated == null ? r.rate : escalated,
      escalationFactor: escalated == null ? null : f,
      escalated: escalated != null,
    };
  });

  const values = contributors
    .map((c) => c.escalatedRate)
    .filter((v) => v != null && !Number.isNaN(Number(v)))
    .map(Number)
    .sort((a, b) => a - b);

  const projects = new Set(
    contributors.map((c) => c.projectId).filter((id) => id != null),
  );

  return {
    sampleSize: values.length,
    distinctProjects: projects.size,
    median: percentileCont(values, 0.5),
    p25: percentileCont(values, 0.25),
    p75: percentileCont(values, 0.75),
    min: values.length ? values[0] : null,
    max: values.length ? values[values.length - 1] : null,
    contributors,
  };
}

// Group rates by (subtradeNodeId, measureBasis) — the natural unit of a
// comparable rate set. Returns a Map keyed "<nodeId>::<basis>" -> rate[].
export function groupBySubtradeBasis(rates) {
  const map = new Map();
  (rates || []).forEach((r) => {
    const key = `${r.subtradeNodeId}::${r.measureBasis}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(r);
  });
  return map;
}

// Quartiles need a few points before they say anything: on one rate P25, P75,
// min and max all collapse to that rate, and on two or three the "spread" is
// just the values themselves. Below QUARTILE_MIN_SAMPLE the quartile cell
// carries a note instead, and the range is shown only once there are two
// values to span. Every view that prints a spread goes through this.
export const QUARTILE_MIN_SAMPLE = 4;

export function spreadDisplay(stats) {
  const n = stats?.sampleSize || 0;
  if (n === 0) return { quartiles: null, range: null, note: null };
  if (n === 1) return { quartiles: null, range: null, note: 'one rate' };
  const range = [stats.min, stats.max];
  if (n < QUARTILE_MIN_SAMPLE) return { quartiles: null, range, note: `only ${n} rates` };
  return { quartiles: [stats.p25, stats.p75], range, note: null };
}
